import React, { FC, useEffect } from "react";
import { useSelector } from "react-redux";

import { Button, Grid, IconButton, Modal, Typography } from "@material-ui/core";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import CreateIcon from "@material-ui/icons/Create";
import LensIcon from "@material-ui/icons/Lens";
import RedoIcon from "@material-ui/icons/Redo";
import ReplyAllIcon from "@material-ui/icons/ReplyAll";
import UndoIcon from "@material-ui/icons/Undo";

import { uploadOriginalPhotoGoogleCloudStorage } from "../../../apis/photoAction";
import { selectImageToken } from "../../../features/userSlice";
import { getFixedFlg } from "../../Header/Header";
import TenpoCheckDialog from "../../TenpoCheck/TenpoCheckDialog";

import Avatar from "@material-ui/core/Avatar";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    uploaded: {
      color: "#37ab9d",
    },
    create: {
      color: "#00cc5b",
    },
    lineColorBlack: {
      color: "black",
    },
    lineColorRed: {
      color: "red",
    },
    lineColorOrange: {
      color: "orange",
    },
    lineColorBlue: {
      color: "blue",
    },
    lineColorGreen: {
      color: "green",
    },
    modalPane: {
      position: "absolute",
      paddingTop: "0%",
      paddingLeft: "0%",
      width: "100%",
      height: "100%",
    },
    paper: {
      height: "100%",
      backgroundColor: "#333333",
      border: "2px solid #000",
      boxShadow: theme.shadows[5],
      padding: theme.spacing(2, 4, 3),
    },
    titleGrid: {
      color: "white",
      backgroundColor: "#4dc0b2",
    },
    titleTypography: {
      fontWeight: "bold",
    },
    editToolArea: {
      height: "400px",
      width: "40px",
    },
    editToolAreaUl: {
      width: "100%",
      listStyle: "none",
      margin: 0,
      padding: 0,
      paddingTop: 10,
      paddingBottom: 10,
      backgroundColor: "#666666",
      borderRadius: "100vh",

      "& li": {
        position: "relative",
        paddingLeft: "10%",
        marginTop: "1px",
      },
    },
  })
);

const PhotoEdit: FC<{
  baseFile: { path: string, size: number };
  updateFile: (file: { path: string, size: number }) => void;
  fileNameGenerator: () => string;
  functionPath: string;
  updateLoading: (isLoading: boolean) => void;
}> = ({
  baseFile,
  updateFile,
  fileNameGenerator,
  functionPath,
  updateLoading,
}) => {
  const classes = useStyles();
  // 各種定数
  const defaultLineWidth: number = 2; // デフォルトの線の太さ
  const thickLineWidth: number = 6; // 太いの線の太さ
  const defaultLineColor: string = "black"; // デフォルトの線の色
  const defaultTransParency: number = 1; // デフォルトの線の透過度
  const halfTransParency: number = 0.3; // ぼかし時の線の透過度
  const cropHandleLength: number = 12; // トリミング用ドラッグポイント辺長
  const cropHandleRangeMagnification: number = 2; // トリミング用ドラッグポイント範囲倍率
  const lineColorKinds: any = {
    // 線の色設定
    black: "black",
    red: "red",
    orange: "orange",
    blue: "blue",
    green: "green",
  };
  const toolItem: any = {
    crop: "crop",
    line: "line",
    lineHalf: "lineHalf",
    eraser: "eraser",
    black: "lineColor_black",
    red: "lineColor_red",
    orange: "lineColor_orange",
    blue: "lineColor_blue",
    green: "lineColor_green",
  };
  const [filePath, setFilePath] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [dialogMessage, setDialogMessage] = React.useState<any[]>([]);
  const [dialogFlg, setDialogFlg] = React.useState(false);
  const [history, setHistory] = React.useState<any[]>([]);
  const [historyIndex, setHistoryIndex] = React.useState<number>(-1);

  interface Point {
    x: number;
    y: number;
  }
  interface CropRect {
    x: number;
    y: number;
    width: number;
    height: number;
  }
  const canvasRef = React.useRef<HTMLCanvasElement>(null); // 画像用
  const lineCanvasRef = React.useRef<HTMLCanvasElement>(null); // 線引き用
  const cropCanvasRef = React.useRef<HTMLCanvasElement>(null); // トリミング用
  const [drawing, setDrawing] = React.useState(false); // マウスをラッグ中であること
  const [lastPoint, setLastPoint] = React.useState<Point>({ x: 0, y: 0 }); // クリック、ドラッグ時のマウスポイント。線を引くときに使用
  const [canvasContext, setCanvasContext] =
    React.useState<CanvasRenderingContext2D | null>(null); // 画像用canvasのcontext
  const [lineCanvasContext, setLineCanvasContext] =
    React.useState<CanvasRenderingContext2D | null>(null); // ライン用canvasのcontext
  const [cropCanvasContext, setCropCanvasContext] =
    React.useState<CanvasRenderingContext2D | null>(null); // 切取り用canvasのcontext
  const [lineWidth, setLineWidth] = React.useState<number>(defaultLineWidth); // 線の太さ
  const [lineColor, setLineColor] = React.useState<string>(
    lineColorKinds.black
  ); // 線の色
  const [createMode, setCreateMode] = React.useState<boolean>(false); // 線を引くモード
  const [eraserMode, setEraserMode] = React.useState<boolean>(false); // 線を消すモード
  const [cropMode, setCropMode] = React.useState<boolean>(false); // 切り取りモード
  const [readyCrop, setReadyCrop] = React.useState<boolean>(false); // 切り取り準備完了有無。切り取り範囲を指定し終わったらtrueとなる
  const [transParency, setTransParency] =
    React.useState<number>(defaultTransParency); // 透過度。線を引くときの線の透過度を管理
  const [initImage, setInitImage] = React.useState<ImageData | null>(null); // 初期画像
  const [cropRect, setCropRect] = React.useState<CropRect>({
    x: 0,
    y: 0,
    width: 200,
    height: 200,
  }); // 切取り範囲
  const [dragHandle, setDragHandle] = React.useState<number | null>(null); // 切取り範囲変更点対象
  // 表示用のスケーリング関連の状態
  const [displayScale, setDisplayScale] = React.useState<number>(1);
  const [canvasDisplaySize, setCanvasDisplaySize] = React.useState<{
    width: number;
    height: number;
  }>({ width: 0, height: 0 });
  // canvas格納箇所
  const containerRef = React.useRef<HTMLDivElement>(null);
  // ズーム時のロック状態
  const [zoomLock, setZoomLock] = React.useState<boolean>(false);

  const dialogClose = () => {
    setDialogFlg(false);
  };
  const handleOpen = () => {
    setOpen(true);
  };

  // モーダル開閉時のファイルパス設定
  useEffect(() => {
    if (open) {
      setFilePath(baseFile ? [baseFile.path] : []);
      resetCrop();
      resetLine();
      resetCreate();
      setCreateMode(true);
      setHistory([]);
      setHistoryIndex(-1);
      refreshCanvas();
      if (!lineCanvasContext) return;
      lineCanvasContext.setLineDash([]);
    } else {
      setCropRect({ x: 0, y: 0, width: 0, height: 0 });
    }
  }, [open]);

  // リサイズイベントのハンドリング
  useEffect(() => {
    if (!open) return;
    
    const handleResize = () => {
      if (filePath && filePath[0]) {
        refreshCanvas();
      }
    };
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [open, filePath]);

  // トリミング範囲変更時
  useEffect(() => {
    if (!cropCanvasContext) return;
    drawOverlay(cropCanvasContext, cropRect);
    if (cropMode) {
      drawCropRect(cropCanvasContext, cropRect);
    }
  }, [cropRect]);

  // 画像変更時。canvasへの画像表示、各種設定初期化。
  useEffect(() => {
    if (filePath && filePath.length > 0 && filePath[0]) {
      // モーダルが開いている時のみ、少し遅延させて実行
      if (open) {
        setTimeout(() => {
          refreshCanvas();
        }, 100);
      }
    }
  }, [filePath, open]);

  // 線の太さ変更
  useEffect(() => {
    if (!lineCanvasContext) return;
    lineCanvasContext.lineWidth = lineWidth;
  }, [lineWidth, lineCanvasContext, history]);

  // 消しゴムモード設定
  useEffect(() => {
    if (!lineCanvasContext) return;
    lineCanvasContext.globalCompositeOperation = eraserMode
      ? "destination-out"
      : "source-over";
  }, [eraserMode, lineCanvasContext, history]);

  // 線の透過度設定
  useEffect(() => {
    if (!lineCanvasContext) return;
    lineCanvasContext.globalAlpha = transParency;
  }, [transParency, lineCanvasContext, history]);

  // 線のカラー
  useEffect(() => {
    if (!lineCanvasContext) return;
    lineCanvasContext.strokeStyle = lineColor;
  }, [lineColor, lineCanvasContext, history]);

  // トリミングモードOFF時トリミング範囲表示初期化
  useEffect(() => {
    if (!cropCanvasContext) return;
    if (cropMode) {
      drawOverlay(cropCanvasContext, cropRect);
      drawCropRect(cropCanvasContext, cropRect);
    } else {
      cropCanvasContext.clearRect(
        0,
        0,
        cropCanvasContext.canvas.width,
        cropCanvasContext.canvas.height
      );
      drawOverlay(cropCanvasContext, cropRect);
    }
  }, [cropMode]);

  // 表示領域のサイズを計算
  const calculateDisplaySize = (
    imgWidth: number,
    imgHeight: number
  ): { width: number; height: number; scale: number } => {
    if (!containerRef.current) {
      // コンテナが取得できない場合はデフォルト値を使用
      const defaultMaxWidth = window.innerWidth * 0.85 - 100; // 画面幅の85% - ツールバーと余白
      const defaultMaxHeight = window.innerHeight * 0.75; // 画面高さの75%
      
      const imgAspect = imgWidth / imgHeight;
      const containerAspect = defaultMaxWidth / defaultMaxHeight;
      
      let displayWidth: number;
      let displayHeight: number;
      let scale: number;
      
      if (imgAspect > containerAspect) {
        // 横長画像：幅を基準にスケーリング
        displayWidth = defaultMaxWidth;
        scale = displayWidth / imgWidth;
        displayHeight = imgHeight * scale;
      } else {
        // 縦長画像：高さを基準にスケーリング
        displayHeight = defaultMaxHeight;
        scale = displayHeight / imgHeight;
        displayWidth = imgWidth * scale;
      }
      
      return { width: displayWidth, height: displayHeight, scale };
    }

    // コンテナのサイズを取得（余白を最小限に）
    const containerRect = containerRef.current.getBoundingClientRect();
    const maxWidth = containerRect.width - 10; // 左右の余白を最小に
    const maxHeight = containerRect.height - 10; // 上下の余白を最小に

    // アスペクト比を維持しながらスケーリング
    const imgAspect = imgWidth / imgHeight;
    const containerAspect = maxWidth / maxHeight;

    let displayWidth: number;
    let displayHeight: number;
    let scale: number;

    if (imgAspect > containerAspect) {
      // 横長画像：幅を基準にスケーリング（常に表示領域に合わせる）
      displayWidth = maxWidth;
      scale = displayWidth / imgWidth;
      displayHeight = imgHeight * scale;
    } else {
      // 縦長画像：高さを基準にスケーリング（常に表示領域に合わせる）
      displayHeight = maxHeight;
      scale = displayHeight / imgHeight;
      displayWidth = imgWidth * scale;
    }

    return { width: displayWidth, height: displayHeight, scale };
  };

  // canvas更新
  const refreshCanvas = (): void => {
    if (!filePath || filePath.length === 0 || !filePath[0]) {
      console.error("No filepath provided for canvas refresh");
      return;
    }

    const img = new Image();
    img.src = getImageFile(filePath[0]);
    img.crossOrigin = "Anonymous";
    
    img.onerror = () => {
      console.error("Failed to load image:", filePath[0]);
    };
    
    img.onload = () => {
      const lineCanvas: HTMLCanvasElement | null = lineCanvasRef.current;

      // リサイズ前に現在の描画内容を一時的に保持
      const tempLineImage = new Image();
      if (lineCanvas) {
        tempLineImage.src = lineCanvas.toDataURL();
      }

      // 表示サイズを計算
      const displayInfo = calculateDisplaySize(img.width, img.height);
      setDisplayScale(displayInfo.scale);
      setCanvasDisplaySize({
        width: displayInfo.width,
        height: displayInfo.height,
      });

      // imageCanvas
      const canvas: any = canvasRef.current;
      if (!canvas) return;
      canvas.width = img.width;
      canvas.height = img.height;
      const context: CanvasRenderingContext2D = canvas.getContext("2d");
      context.imageSmoothingEnabled = false;
      context.drawImage(img, 0, 0, img.width, img.height);

      // lineCanvas
      if (!lineCanvas) return;
      lineCanvas.width = img.width;
      lineCanvas.height = img.height;
      const lineContext: CanvasRenderingContext2D | null =
        lineCanvas.getContext("2d");
      
      if (lineContext) {
        // 保持しておいた描画内容を復元
        tempLineImage.onload = () => {
          lineContext.drawImage(tempLineImage, 0, 0);
        };
        
        // ペンの設定を再適用
        lineContext.lineWidth = lineWidth;
        lineContext.globalCompositeOperation = eraserMode
          ? "destination-out"
          : "source-over";
        lineContext.globalAlpha = transParency;
        lineContext.strokeStyle = lineColor;
      }
      setLineCanvasContext(lineContext);

      // cropCanvas
      const cropCanvas: HTMLCanvasElement | null = cropCanvasRef.current;
      if (!cropCanvas) return;
      cropCanvas.width = img.width;
      cropCanvas.height = img.height;
      const cropContext: CanvasRenderingContext2D | null =
        cropCanvas.getContext("2d");
      setCropCanvasContext(cropContext);

      setDrawing(false);
      setCanvasContext(context);
      setInitImage(context.getImageData(0, 0, img.width, img.height));
      const initialCropRect = { x: 0, y: 0, width: img.width, height: img.height };
      setCropRect(initialCropRect);
      if (context && lineContext) {
        addHistory(context, lineContext, initialCropRect);
      }
    };
  };

  // 編集アイコンの選択状態CSS取得
  const selectToolCss = (item: string): string => {
    let selected: boolean = false;
    if (item === "crop") {
      selected = cropMode;
    } else if (item === "line") {
      selected = createMode && transParency === defaultTransParency;
    } else if (item === "lineHalf") {
      selected = createMode && transParency !== defaultTransParency;
    } else if (item === "eraser") {
      selected = eraserMode;
    } else if (item.startsWith("lineColor")) {
      selected = item === "lineColor_" + lineColor;
    } else if (item === "lineWidthDefault") {
      selected = lineWidth === defaultLineWidth;
    } else if (item === "lineWidthThick") {
      selected = lineWidth === thickLineWidth;
    }
    return selected ? "photo-edit-li-selected" : "photo-edit-li";
  };

  // 表示座標から実際のcanvas座標に変換
  const convertToCanvasCoordinates = (
    displayX: number,
    displayY: number
  ): { x: number; y: number } => {
    return {
      x: displayX / displayScale,
      y: displayY / displayScale,
    };
  };

  // タッチの始め
  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (!canvasContext) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const displayX = e.touches[0].clientX - rect.left;
    const displayY = e.touches[0].clientY - rect.top;
    
    if (displayX < 0 || displayY < 0) {
      setZoomLock(false);
      return;
    } else {
      setZoomLock(true);
    }
    
    // 表示座標から実際のcanvas座標に変換
    const canvasCoords = convertToCanvasCoordinates(displayX, displayY);
    startOperation(canvasCoords.x, canvasCoords.y);
  };

  // タッチの終わり
  const handleTouchEnd = (e: React.TouchEvent<HTMLCanvasElement>) => {
    endOperation();
    e.preventDefault();
  };

  // タッチで線を引く、クラップ範囲の指定等を行う。
  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    // 座標位置取得が必要なモードになっていないとき
    if (!drawing || (!createMode && !eraserMode && !cropMode)) {
      return;
    }
    // 二本指でのタッチ時
    if (e.touches.length >= 2){
      return;
    }

    if (!canvasContext) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const displayX = e.changedTouches[0].clientX - rect.left;
    const displayY = e.changedTouches[0].clientY - rect.top;
    
    // 表示座標から実際のcanvas座標に変換
    const canvasCoords = convertToCanvasCoordinates(displayX, displayY);
    moveOperation(canvasCoords.x, canvasCoords.y);
    e.preventDefault();
  };

  // ドラッグの始め
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const displayX = e.clientX - rect.left;
    const displayY = e.clientY - rect.top;
    
    // 表示座標から実際のcanvas座標に変換
    const canvasCoords = convertToCanvasCoordinates(displayX, displayY);
    startOperation(canvasCoords.x, canvasCoords.y);
  };

  // ドラッグの終わり
  const handleMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    endOperation();
  };

  // ドラッグ中。線を引く、クラップ範囲の指定等を行う。
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    // 座標位置取得が必要なモードになっていないとき
    if (!drawing || (!createMode && !eraserMode && !cropMode)) {
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const displayX = e.clientX - rect.left;
    const displayY = e.clientY - rect.top;
    
    // 表示座標から実際のcanvas座標に変換
    const canvasCoords = convertToCanvasCoordinates(displayX, displayY);
    moveOperation(canvasCoords.x, canvasCoords.y);
  };

  // 操作開始時（マウスクリック、タッチ）
  const startOperation = (offsetX: number, offsetY: number): void => {
    const { x, y, width, height } = cropRect;
    const operationLength = cropHandleLength * cropHandleRangeMagnification;

    if (cropMode) {
      if (
        offsetX >= x - cropHandleLength &&
        offsetX <= x + operationLength &&
        offsetY >= y - cropHandleLength &&
        offsetY <= y + operationLength
      ) {
        // 左上のドラッグ
        setDragHandle(0);
      } else if (
        offsetX >= x + width - operationLength &&
        offsetX <= x + width + cropHandleLength &&
        offsetY >= y - cropHandleLength &&
        offsetY <= y + operationLength
      ) {
        // 右上のドラッグ
        setDragHandle(1);
      } else if (
        offsetX >= x + width - operationLength &&
        offsetX <= x + width + cropHandleLength &&
        offsetY >= y + height - operationLength &&
        offsetY <= y + height + cropHandleLength
      ) {
        // 右下のドラッグ
        setDragHandle(2);
      } else if (
        offsetX >= x - cropHandleLength &&
        offsetX <= x + operationLength &&
        offsetY >= y + height - operationLength &&
        offsetY <= y + height + cropHandleLength
      ) {
        // 左下のドラッグ
        setDragHandle(3);
      } else if (
        offsetX >= x + width / 2 - cropHandleLength &&
        offsetX <= x + width / 2 + cropHandleLength &&
        offsetY >= y - cropHandleLength &&
        offsetY <= y + operationLength
      ) {
        // 上のドラッグ
        setDragHandle(4);
      } else if (
        offsetX >= x + width - operationLength &&
        offsetX <= x + width + cropHandleLength &&
        offsetY >= y + height / 2 - cropHandleLength &&
        offsetY <= y + height / 2 + cropHandleLength
      ) {
        // 右のドラッグ
        setDragHandle(5);
      } else if (
        offsetX >= x + width / 2 - cropHandleLength &&
        offsetX <= x + width / 2 + cropHandleLength &&
        offsetY >= y + height - operationLength &&
        offsetY <= y + height + cropHandleLength
      ) {
        // 下のドラッグ
        setDragHandle(6);
      } else if (
        offsetX >= x - cropHandleLength &&
        offsetX <= x + operationLength &&
        offsetY >= y + height / 2 - cropHandleLength &&
        offsetY <= y + height / 2 + cropHandleLength
      ) {
        // 左のドラッグ
        setDragHandle(7);
      }
    }
    setLastPoint({ x: offsetX, y: offsetY });
    setDrawing(true);
  };

  // 操作終了時（マウスクリック、タッチ）
  const endOperation = (): void => {
    setDrawing(false);
    if (cropMode) {
      setReadyCrop(true);
      setDragHandle(null);
    }
    if (createMode || eraserMode || cropMode) {
      if (!canvasContext || !lineCanvasContext) return;
      addHistory(canvasContext, lineCanvasContext, cropRect);
    }
  };

  // 操作時（マウスクリック、タッチ）
  const moveOperation = (offsetX: number, offsetY: number): void => {
    if (cropMode) {
      moveCropOperation(offsetX, offsetY);
    } else {
      if (!canvasContext || !lineCanvasContext) return;
      const canvas: HTMLCanvasElement = canvasContext.canvas;
      // 座標補正
      if (offsetX < 0) {
        offsetX = 0;
      } else if (canvas.width < offsetX) {
        offsetX = canvas.width;
      }
      if (offsetY < 0) {
        offsetY = 0;
      } else if (canvas.height < offsetY) {
        offsetY = canvas.height;
      }
      const currentPoint = {
        x: offsetX,
        y: offsetY,
      };

      lineCanvasContext.beginPath();
      lineCanvasContext.moveTo(lastPoint.x, lastPoint.y);
      lineCanvasContext.lineTo(currentPoint.x, currentPoint.y);
      lineCanvasContext.stroke();
      setLastPoint(currentPoint);
    }
  };

  // トリミング操作時（マウスクリック、タッチ）
  const moveCropOperation = (offsetX: number, offsetY: number): void => {
    let currentPoint = {
      x: offsetX,
      y: offsetY,
    };

    if (!cropCanvasContext || dragHandle === null) return;
    const xDiff = currentPoint.x - lastPoint.x;
    const yDiff = currentPoint.y - lastPoint.y;
    let updateCropRect = { ...cropRect };

    switch (dragHandle) {
      case 0: // 左上
        updateCropRect.x += xDiff;
        updateCropRect.y += yDiff;
        updateCropRect.width -= xDiff;
        updateCropRect.height -= yDiff;
        break;
      case 1: // 右上
        updateCropRect.y += yDiff;
        updateCropRect.width += xDiff;
        updateCropRect.height -= yDiff;
        break;
      case 2: // 右下
        updateCropRect.width += xDiff;
        updateCropRect.height += yDiff;
        break;
      case 3: // 左下
        updateCropRect.x += xDiff;
        updateCropRect.width -= xDiff;
        updateCropRect.height += yDiff;
        break;
      case 4: // 上
        updateCropRect.y += yDiff;
        updateCropRect.height -= yDiff;
        break;
      case 5: // 右
        updateCropRect.width += xDiff;
        break;
      case 6: // 下
        updateCropRect.height += yDiff;
        break;
      case 7: // 左
        updateCropRect.x += xDiff;
        updateCropRect.width -= xDiff;
        break;
    }

    // 範囲外かを判定
    const range = {
      x: 0,
      y: 0,
      width: cropCanvasContext.canvas.width,
      height: cropCanvasContext.canvas.height,
    };
    const within = isWithin(updateCropRect, range);

    // 異常値回避、範囲外に出た場合の対応
    if (updateCropRect.x < 0) {
      updateCropRect.x = 0;
    } else if (updateCropRect.x > cropCanvasContext.canvas.width) {
      updateCropRect.x = cropCanvasContext.canvas.width;
    } else if (updateCropRect.y < 0) {
      updateCropRect.y = 0;
    } else if (updateCropRect.y > cropCanvasContext.canvas.height) {
      updateCropRect.y = cropCanvasContext.canvas.height;
    } else if (updateCropRect.width < 0) {
      updateCropRect.width = 0;
    } else if (updateCropRect.width > cropCanvasContext.canvas.width) {
      updateCropRect.width = cropCanvasContext.canvas.width;
    } else if (updateCropRect.height < 0) {
      updateCropRect.height = 0;
    } else if (updateCropRect.height > cropCanvasContext.canvas.height) {
      updateCropRect.height = cropCanvasContext.canvas.height;
    }

    setCropRect(updateCropRect);
    setLastPoint(currentPoint);

    // 範囲外に出た場合は終了
    if (!within) {
      setDrawing(false);
      setReadyCrop(true);
      setDragHandle(null);
    }
  };

  // 対象が範囲内か判定
  const isWithin = (target: CropRect, range: CropRect): boolean => {
    let ret = true;
    if (
      target.x < range.x ||
      target.x > range.width ||
      target.y < range.y ||
      target.y > range.height ||
      target.width < 0 ||
      target.width > range.width ||
      target.height < 0 ||
      target.height > range.height
    ) {
      ret = false;
    }
    return ret;
  };

  // トリミング範囲描画
  const drawCropRect = (
    ctx: CanvasRenderingContext2D,
    rect: CropRect
  ): void => {
    const storeStrokeStyle = ctx.strokeStyle;
    const storeLineWidth = ctx.lineWidth;
    const storeFillStyle = ctx.fillStyle;

    // 対象範囲囲み線
    ctx.strokeStyle = "deepskyblue";
    ctx.lineWidth = 1;
    const { x, y, width, height } = rect;
    ctx.strokeRect(x, y, width, height);

    // 対象範囲移動点_塗りつぶし
    const half = cropHandleLength / 2;
    ctx.fillStyle = "white";
    ctx.lineWidth = 2;
    // 左上
    ctx.fillRect(x, y, cropHandleLength, cropHandleLength);
    // 右上
    ctx.fillRect(
      x + width - cropHandleLength,
      y,
      cropHandleLength,
      cropHandleLength
    );
    // 右下
    ctx.fillRect(
      x + width - cropHandleLength,
      y + height - cropHandleLength,
      cropHandleLength,
      cropHandleLength
    );
    // 左下
    ctx.fillRect(
      x,
      y + height - cropHandleLength,
      cropHandleLength,
      cropHandleLength
    );
    // 上
    ctx.fillRect(x + width / 2 - half, y, cropHandleLength, cropHandleLength);
    // 右
    ctx.fillRect(
      x + width - cropHandleLength,
      y + height / 2 - half,
      cropHandleLength,
      cropHandleLength
    );
    // 下
    ctx.fillRect(
      x + width / 2 - half,
      y + height - cropHandleLength,
      cropHandleLength,
      cropHandleLength
    );
    // 左
    ctx.fillRect(x, y + height / 2 - half, cropHandleLength, cropHandleLength);

    // 対象範囲移動点_囲み
    ctx.strokeStyle = "deepskyblue";
    ctx.lineWidth = 1;
    // 左上
    ctx.strokeRect(x, y, cropHandleLength, cropHandleLength);
    // 右上
    ctx.strokeRect(
      x + width - cropHandleLength,
      y,
      cropHandleLength,
      cropHandleLength
    );
    // 右下
    ctx.strokeRect(
      x + width - cropHandleLength,
      y + height - cropHandleLength,
      cropHandleLength,
      cropHandleLength
    );
    // 左下
    ctx.strokeRect(
      x,
      y + height - cropHandleLength,
      cropHandleLength,
      cropHandleLength
    );
    // 上
    ctx.strokeRect(x + width / 2 - half, y, cropHandleLength, cropHandleLength);
    // 右
    ctx.strokeRect(
      x + width - cropHandleLength,
      y + height / 2 - half,
      cropHandleLength,
      cropHandleLength
    );
    // 下
    ctx.strokeRect(
      x + width / 2 - half,
      y + height - cropHandleLength,
      cropHandleLength,
      cropHandleLength
    );
    // 左
    ctx.strokeRect(
      x,
      y + height / 2 - half,
      cropHandleLength,
      cropHandleLength
    );

    // 設定を戻す
    ctx.strokeStyle = storeStrokeStyle;
    ctx.lineWidth = storeLineWidth;
    ctx.fillStyle = storeFillStyle;
  };

  // トリミング範囲外部分覆う
  const drawOverlay = (ctx: CanvasRenderingContext2D, rect: CropRect): void => {
    const { x, y, width, height } = rect;
    ctx.clearRect(x, y, width, height);
    const storeFillStyle = ctx.fillStyle;
    ctx.fillStyle = "#333333";
    ctx.fillRect(0, 0, ctx.canvas.width, y);
    ctx.fillRect(0, y, x, height);
    ctx.fillRect(x + width, y, ctx.canvas.width - (x + width), height);
    ctx.fillRect(
      0,
      y + height,
      ctx.canvas.width,
      ctx.canvas.height - (y + height)
    );
    ctx.fillStyle = storeFillStyle;
  };

  // create系リセット
  const resetCreate = (): void => {
    setCreateMode(false);
    setCropMode(false);
    setEraserMode(false);
    setTransParency(defaultTransParency);
  };

  const handleCreateMode = (isMode: boolean) => {
    if (isMode) {
      resetCreate();
    }
    setCreateMode(isMode);
  };

  const handleEraserMode = (isMode: boolean) => {
    if (isMode) {
      resetCreate();
    }
    setEraserMode(isMode);
  };

  const handleCropMode = (isMode: boolean) => {
    if (isMode) {
      resetCreate();
    }
    setCropMode(isMode);
  };

  // 保存処理。canvasで編集した内容で画像をアップロードし直す
  const handleSave = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    if (getFixedFlg()) {
      setOpen(false);
      return;
    }

    if (!canvasContext || !lineCanvasContext) return;
    // トリミング実施
    if (readyCrop) {
      addHistory(canvasContext, lineCanvasContext, cropRect);
      await cropImage();
    }

    // モーダルを閉じる
    setOpen(false);

    // 画像レイヤーに描画した線を合成
    canvasContext.drawImage(lineCanvasContext.canvas, 0, 0);

    // 保存時に画像をアップロード
    const fileName = fileNameGenerator();
    const newFile = await uploadPhoto(functionPath, fileName);

    updateFile(newFile);
  };

  // 画像アップロード
  const uploadPhoto = (
    functionFilePath: string,
    fileName: string
  ): Promise<{ path: string, size: number }> => {
    return new Promise((resolve, reject) => {
      if (!canvasContext) {
        resolve({ path: "", size: 0 });
        return;
      }
      updateLoading(true);
      const canvas: HTMLCanvasElement = canvasContext.canvas;
      canvas.toBlob(async (blob) => {
        if (!blob) {
          resolve({ path: "", size: 0 });
          return;
        }
        // 新しいアップロード関数を呼び出す
        const result = await uploadOriginalPhotoGoogleCloudStorage(
          blob,
          functionFilePath,
          fileName
        );

        let newFile = { path: "", size: 0 };
        if (!result || !result.downloadURL) {
          if (result.downloadURL === "") {
            setDialogMessage(["画像のアップロードに失敗しました"]);
            setDialogFlg(true);
          }
        } else {
          newFile = {
            path: String(functionFilePath + fileName),
            size: result.fileSize
          };
        }
        updateLoading(false);
        resolve(newFile);
      }, "image/jpeg", 0.8);
    });
  };

  // キャンセル時の動作
  const handleClose = () => {
    if (loading) {
      return;
    }
    setOpen(false);
  };

  // 元の画像状態に戻す
  const replyAll = () => {
    if (!canvasContext || !initImage || !lineCanvasContext) return;
    canvasContext.putImageData(initImage, 0, 0);
    // canvasをクリア
    const lineCanvas = lineCanvasContext.canvas;
    lineCanvas.width = lineCanvas.width;
    
    // canvasコンテキストのプロパティを再設定
    lineCanvasContext.globalCompositeOperation = eraserMode
      ? "destination-out"
      : "source-over";
    lineCanvasContext.globalAlpha = transParency;
    lineCanvasContext.strokeStyle = lineColor;
    lineCanvasContext.lineWidth = lineWidth;
    setHistory([]);
    setHistoryIndex(-1);
    if (canvasContext && lineCanvasContext && cropCanvasContext) {
      const { width, height } = cropCanvasContext.canvas;
      const initialCropRect = { x: 0, y: 0, width: width, height: height };
      addHistory(canvasContext, lineCanvasContext, initialCropRect);
    }
    if (!cropCanvasContext) return;
    const { width, height } = cropCanvasContext.canvas;
    setCropRect({ x: 0, y: 0, width: width, height: height });
  };

  const imageToken = useSelector(selectImageToken);
  function getImageFile(imagePath: string) {
    if (imagePath === undefined || imagePath === "") {
      return `${window.location.origin}/image/noimage.png`;
    }
    return (
      process.env.REACT_APP_CLOUD_STORAGE_URL +
      imagePath +
      "?access_token=" +
      imageToken
    );
  }

  // 画像を切り取り
  const cropImage = async () => {
    if (
      !canvasContext ||
      !lineCanvasContext ||
      !cropCanvasContext ||
      !readyCrop
    )
      return;

    const { x, y, width, height } = cropRect;
    
    // 一時的なCanvasを作成してクロップ処理
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = width;
    tempCanvas.height = height;
    const tempContext = tempCanvas.getContext('2d');
    if (!tempContext) return;
    tempContext.imageSmoothingEnabled = false;

    // 画像レイヤーのクロップ部分を一時Canvasにコピー
    tempContext.drawImage(
      canvasContext.canvas,
      x,
      y,
      width,
      height,
      0,
      0,
      width,
      height
    );

    // ラインレイヤーのクロップ処理
    const tempLineCanvas = document.createElement('canvas');
    tempLineCanvas.width = width;
    tempLineCanvas.height = height;
    const tempLineContext = tempLineCanvas.getContext('2d');
    if (!tempLineContext) return;
    tempLineContext.imageSmoothingEnabled = false;

    tempLineContext.drawImage(
      lineCanvasContext.canvas,
      x,
      y,
      width,
      height,
      0,
      0,
      width,
      height
    );

    // Canvasのサイズを変更
    const canvas = canvasRef.current;
    const lineCanvas = lineCanvasRef.current;
    const cropCanvas = cropCanvasRef.current;
    
    if (!canvas || !lineCanvas || !cropCanvas) return;

    // 新しいサイズに変更
    canvas.width = width;
    canvas.height = height;
    lineCanvas.width = width;
    lineCanvas.height = height;
    cropCanvas.width = width;
    cropCanvas.height = height;

    // クロップした画像を描画
    canvasContext.drawImage(tempCanvas, 0, 0);
    lineCanvasContext.drawImage(tempLineCanvas, 0, 0);

    // 初期画像を更新
    setInitImage(canvasContext.getImageData(0, 0, width, height));

    // クロップ範囲をリセット
    setCropRect({ x: 0, y: 0, width: width, height: height });
    
    // 表示サイズを再計算
    const displayInfo = calculateDisplaySize(width, height);
    setDisplayScale(displayInfo.scale);
    setCanvasDisplaySize({
      width: displayInfo.width,
      height: displayInfo.height,
    });

    // クロップモードを解除
    setCropMode(false);
    setReadyCrop(false);
    // ペンモードを再度有効化する
    handleCreateMode(true);
  };

  // 画像読み込み
  const loadImage = async (src: string): Promise<HTMLImageElement> => {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.onload = function () {
        resolve(img);
      };
      img.onerror = function () {
        reject(new Error("画像のロードに失敗しました"));
      };
      img.src = src;
    });
  };

  // crop関連reset
  const resetCrop = (): void => {
    setCropMode(false);
    setReadyCrop(false);
  };

  // 線描画関連reset
  const resetLine = (): void => {
    setLineWidth(defaultLineWidth);
    setLineColor(defaultLineColor);
    setTransParency(defaultTransParency);
  };

  // 履歴追加
  const addHistory = (
    cCtx: CanvasRenderingContext2D,
    lCtx: CanvasRenderingContext2D,
    cr: CropRect
  ) => {
    const newHistory = history.slice(0, historyIndex + 1);
    const image = cCtx.getImageData(0, 0, cCtx.canvas.width, cCtx.canvas.height);
    const line = lCtx.getImageData(0, 0, lCtx.canvas.width, lCtx.canvas.height);
    newHistory.push({ image: image, line: line, cropRect: cr });
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  // 戻る
  const handleUndo = () => {
    if (historyIndex <= 0 || !canvasContext || !lineCanvasContext) return;
    const newIndex = historyIndex - 1;
    const past = history[newIndex];
    if (canvasContext && lineCanvasContext && past) {
      canvasContext.canvas.width = past.image.width;
      canvasContext.canvas.height = past.image.height;
      lineCanvasContext.canvas.width = past.line.width;
      lineCanvasContext.canvas.height = past.line.height;
      canvasContext.putImageData(past.image, 0, 0);
      lineCanvasContext.putImageData(past.line, 0, 0);
      setCropRect(past.cropRect);

      // キャンバスのサイズ変更で描画設定がリセットされるため、再適用
      lineCanvasContext.lineWidth = lineWidth;
      lineCanvasContext.strokeStyle = lineColor;
      lineCanvasContext.globalCompositeOperation = eraserMode
        ? "destination-out"
        : "source-over";
      lineCanvasContext.globalAlpha = transParency;
    }
    setHistoryIndex(newIndex);
  };

  // 進む
  const handleRedo = () => {
    if (
      historyIndex >= history.length - 1 ||
      !canvasContext ||
      !lineCanvasContext
    )
      return;
    const newIndex = historyIndex + 1;
    const future = history[newIndex];
    if (canvasContext && lineCanvasContext && future) {
      canvasContext.canvas.width = future.image.width;
      canvasContext.canvas.height = future.image.height;
      lineCanvasContext.canvas.width = future.line.width;
      lineCanvasContext.canvas.height = future.line.height;
      canvasContext.putImageData(future.image, 0, 0);
      lineCanvasContext.putImageData(future.line, 0, 0);
      setCropRect(future.cropRect);

      // キャンバスのサイズ変更で描画設定がリセットされるため、再適用
      lineCanvasContext.lineWidth = lineWidth;
      lineCanvasContext.strokeStyle = lineColor;
      lineCanvasContext.globalCompositeOperation = eraserMode
        ? "destination-out"
        : "source-over";
      lineCanvasContext.globalAlpha = transParency;
    }
    setHistoryIndex(newIndex);
  };

  return (
    <>
      {/*  編集ボタンここから↓*/}
      <IconButton
        component="span"
        onClick={handleOpen}
        className={classes.uploaded}
        size={"small"}
        style={{ marginLeft: "8px" }}
      >
        <CreateIcon fontSize="default" />
      </IconButton>
      {/*  編集ボタンここまで↑*/}
      {/*  モーダルここから↓*/}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="photo-edit-title"
        aria-describedby="photo-edit-description"
        className={classes.modalPane}
      >
        <Grid
          container
          direction="column"
          className={classes.paper}
          style={{ overflowY: "auto" }}
        >
          <div style={{ height: "100%", minHeight: "500px" }}>
            {/*  タイトルここから↓*/}
            <Grid
              item
              justify="center"
              alignItems="center"
              className={classes.titleGrid}
              style={{ flexBasis: "7%" }}
            >
              <Grid
                container
                justify="center"
                alignItems="center"
                style={{ height: "100%" }}
              >
                <Typography variant="h6" className={classes.titleTypography}>
                  画像編集
                </Typography>
              </Grid>
            </Grid>
            {/*  タイトルここまで↑*/}
            {/*  画像編集エリアここから↓*/}
            <Grid item style={{ flexBasis: "85%", height: "85%" }}>
              <Grid container style={{ paddingTop: "10px", height: "100%" }}>
                {/*  画像編集ツールここから↓*/}
                <Grid item xs={1}>
                  <div id="tool-area" className={classes.editToolArea}>
                    <ul
                      style={{ width: "100%" }}
                      className={classes.editToolAreaUl}
                    >
                      <li>
                        <IconButton onClick={handleUndo} size={"small"}>
                          <UndoIcon fontSize={"default"} />
                        </IconButton>
                      </li>
                      <li>
                        <IconButton onClick={handleRedo} size={"small"}>
                          <RedoIcon fontSize={"default"} />
                        </IconButton>
                      </li>
                      <li style={{ marginLeft: "-5px" }}>
                        <hr style={{ width: "70%", height: "1px", background: "#fff" }}></hr>
                      </li>
                      <li className={selectToolCss("crop")}>
                        <IconButton
                          onClick={() => handleCropMode(true)}
                          size={"small"}
                        >
                          <Avatar
                            alt=""
                            src={`${window.location.origin}/image/icons/crop.svg`}
                            style={{ width: "25px", height: "25px" }}
                          />
                        </IconButton>
                      </li>
                    </ul>
                    <div style={{height: "20px"}}></div>
                    <ul
                      style={{ width: "100%" }}
                      className={classes.editToolAreaUl}
                    >
                      <li className={selectToolCss("line")}>
                        <IconButton
                          onClick={() => {
                            handleCreateMode(true);
                            setTransParency(defaultTransParency);
                          }}
                          size={"small"}
                        >
                          <Avatar
                            alt=""
                            src={`${window.location.origin}/image/icons/thin_pen.svg`}
                            style={{ width: "25px", height: "25px" }}
                          />
                        </IconButton>
                      </li>
                      <li className={selectToolCss("lineHalf")}>
                        <IconButton
                          onClick={(e) => {
                            handleCreateMode(true);
                            setTransParency(halfTransParency);
                          }}
                          size={"small"}
                        >
                          <Avatar
                            alt=""
                            src={`${window.location.origin}/image/icons/thick_pen.svg`}
                            style={{ width: "25px", height: "25px" }}
                          />
                        </IconButton>
                      </li>
                      <li className={selectToolCss("eraser")}>
                        <IconButton
                          onClick={() => handleEraserMode(true)}
                          size={"small"}
                        >
                          <Avatar
                            alt=""
                            src={`${window.location.origin}/image/icons/eraser.svg`}
                            style={{ width: "25px", height: "25px" }}
                          />
                        </IconButton>
                      </li>
                      <li style={{ marginLeft: "-5px" }}>
                        <hr style={{ width: "70%", height: "1px", background: "#fff" }}></hr>
                      </li>
                      <li className={selectToolCss("lineWidthDefault")}>
                        <IconButton
                          onClick={() => setLineWidth(defaultLineWidth)}
                          size={"small"}
                        >
                          <Avatar
                            alt=""
                            src={`${window.location.origin}/image/icons/thin_pencil.svg`}
                            style={{ width: "25px", height: "25px" }}
                          />
                        </IconButton>
                      </li>
                      <li className={selectToolCss("lineWidthThick")}>
                        <IconButton
                          onClick={() => setLineWidth(thickLineWidth)}
                          size={"small"}
                        >
                          <Avatar
                            alt=""
                            src={`${window.location.origin}/image/icons/thick_pencil.svg`}
                            style={{ width: "25px", height: "25px" }}
                          />
                        </IconButton>
                      </li>
                      <li style={{ marginLeft: "-5px" }}>
                        <hr style={{ width: "70%", height: "1px", background: "#fff" }}></hr>
                      </li>
                      <li
                        className={selectToolCss(
                          "lineColor_" + lineColorKinds.black
                        )}
                      >
                        <IconButton
                          onClick={(e) => {
                            setLineColor(lineColorKinds.black);
                          }}
                          size={"small"}
                          className={classes.lineColorBlack}
                        >
                          <LensIcon fontSize={"default"} />
                        </IconButton>
                      </li>
                      <li
                        className={selectToolCss(
                          "lineColor_" + lineColorKinds.red
                        )}
                      >
                        <IconButton
                          onClick={(e) => {
                            setLineColor(lineColorKinds.red);
                          }}
                          size={"small"}
                          className={classes.lineColorRed}
                        >
                          <LensIcon fontSize={"default"} />
                        </IconButton>
                      </li>
                      <li
                        className={selectToolCss(
                          "lineColor_" + lineColorKinds.orange
                        )}
                      >
                        <IconButton
                          onClick={(e) => {
                            setLineColor(lineColorKinds.orange);
                          }}
                          size={"small"}
                          className={classes.lineColorOrange}
                        >
                          <LensIcon fontSize={"default"} />
                        </IconButton>
                      </li>
                      <li
                        className={selectToolCss(
                          "lineColor_" + lineColorKinds.blue
                        )}
                      >
                        <IconButton
                          onClick={(e) => {
                            setLineColor(lineColorKinds.blue);
                          }}
                          size={"small"}
                          className={classes.lineColorBlue}
                        >
                          <LensIcon fontSize={"default"} />
                        </IconButton>
                      </li>
                      <li
                        className={selectToolCss(
                          "lineColor_" + lineColorKinds.green
                        )}
                      >
                        <IconButton
                          onClick={(e) => {
                            setLineColor(lineColorKinds.green);
                          }}
                          size={"small"}
                          className={classes.lineColorGreen}
                        >
                          <LensIcon fontSize={"default"} />
                        </IconButton>
                      </li>
                      <li className={selectToolCss("")}>
                        <IconButton
                          onClick={(e) => {
                            replyAll();
                          }}
                          size={"small"}
                        >
                          <ReplyAllIcon fontSize={"default"} />
                        </IconButton>
                      </li>
                    </ul>
                  </div>
                </Grid>
                {/*  画像編集ツールここまで↑*/}
                {/*  画像編集canvasここから↓*/}
                <Grid item xs={11}>
                  <div
                    ref={containerRef}
                    style={{
                      position: "relative",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                      overflow: "hidden",
                    }}
                  >
                    <canvas
                      className="canvas"
                      ref={canvasRef}
                      style={{ 
                        position: "absolute", 
                        zIndex: 1,
                        width: canvasDisplaySize.width,
                        height: canvasDisplaySize.height,
                      }}
                    />
                    <canvas
                      className="canvas"
                      ref={lineCanvasRef}
                      style={{ 
                        position: "absolute", 
                        zIndex: 2,
                        width: canvasDisplaySize.width,
                        height: canvasDisplaySize.height,
                      }}
                    />
                    <canvas
                      className="canvas"
                      onMouseDown={handleMouseDown}
                      onMouseMove={handleMouseMove}
                      onMouseUp={handleMouseUp}
                      onTouchMove={handleTouchMove}
                      onTouchStart={handleTouchStart}
                      onTouchEnd={handleTouchEnd}
                      ref={cropCanvasRef}
                      style={{ 
                        position: "absolute", 
                        zIndex: 3, 
                        userSelect: "none", 
                        WebkitTouchCallout: "none", 
                        WebkitUserSelect: "none",
                        touchAction: zoomLock ? "pinch-zoom" :"auto",
                        width: canvasDisplaySize.width,
                        height: canvasDisplaySize.height,
                      }}
                    />
                  </div>
                </Grid>
                {/*  画像編集canvasここまで↑*/}
              </Grid>
            </Grid>
            {/*  画像編集エリアここまで↑*/}
            {/*  ボタン↓ここから*/}
            <Grid item style={{ flexBasis: "8%" }}>
              <Grid container justify="flex-end">
                <Grid item xs={6}>
                  <Grid container justify="center">
                    <Button
                      style={{ width: "30vw" }}
                      variant="contained"
                      fullWidth={true}
                      onClick={handleClose}
                      disabled={loading}
                    >
                      キャンセル
                    </Button>
                  </Grid>
                </Grid>
                <Grid item xs={6}>
                  <Grid container justify="center">
                    {getFixedFlg() ? (
                      ""
                    ) : (
                      <Button
                        style={{ width: "30vw" }}
                        variant="contained"
                        fullWidth={true}
                        onClick={(e) => handleSave(e)}
                        color="primary"
                        disabled={loading}
                      >
                        保存
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            {/*  ボタン↑ここまで*/}
          </div>
        </Grid>
      </Modal>
      {/*  モーダル↑ここまで*/}
      {/* ダイアログ */}
      <TenpoCheckDialog
        msg={dialogMessage}
        isOpen={dialogFlg}
        doOK={dialogClose}
        doNo={dialogClose}
        isAlert={true}
      />
    </>
  );
};
export default PhotoEdit;
